import type { TooltipContentProps } from 'recharts'

import { Text } from '@buffer-mono/popcorn'

import { ChartColorDot } from '../ChartColorDot'
import { useChartSeriesById } from '../ChartSeriesContext'
import { formatChartValue } from '../chartFormatting'

import { isRankedRow } from './buildRankedRows'

import styles from './BarChart.module.css'

import type { JSX } from 'react'

type BarTooltipViewProps = Pick<
  Partial<TooltipContentProps<number, string>>,
  'active' | 'payload'
>

/**
 * Context-driven tooltip for the ranked bar chart. Reads the hovered row from
 * Recharts and looks its series up in {@link ChartSeriesContext} for the colour,
 * icon, and label — so the tooltip can't drift from the bar it describes. Shows
 * the channel and its value (formatted per the series' unit).
 */
export function BarTooltipView({
  active,
  payload,
}: BarTooltipViewProps): JSX.Element | null {
  const raw: unknown = payload?.[0]?.payload
  const row = isRankedRow(raw) ? raw : undefined
  const series = useChartSeriesById(row?.id ?? '')

  if (!active || row == null || series == null) return null

  return (
    <div className={styles.tooltip}>
      <div className={styles.tooltipRow}>
        <ChartColorDot color={series.color} />
        {series.icon}
        <Text size="sm">{series.label}</Text>
        <Text size="sm" weight="bold" className={styles.tooltipValue}>
          {formatChartValue(row.value, series.unit)}
        </Text>
      </div>
    </div>
  )
}
